import type { IconsAlignEnum } from '@vedalib/editor/lib/brand'
import { AnimationTypeCardEnum, FlipMethodCardEnum, SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldRadio,
  FieldSegmented,
  FieldSelect,
  FieldSlider,
} from 'components/formKit'
import { FormField } from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import {
  BORDER_OPTIONS,
  ICON_ALIGN_OPTIONS,
  VERTICAL_ALIGN_OPTIONS,
} from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'
import { getEnumOption } from 'utils/enum'

import type { CardFormBrandType } from './cardTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.card.style, {
  card: {
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      props: { width: 64 },
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 40, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
  },
  border: {
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  icon: {
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 100, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  sideA: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    alignItems: genBrandField({
      as: FieldRadio<'flex-start' | 'center' | 'flex-end'>,
      label: t('input.label.alignment'),
      props: {
        options: VERTICAL_ALIGN_OPTIONS,
        hideRadio: true,
        fluid: false,
        direction: 'horizontal',
      },
    }),
  },
  sideAIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.card.tags.sides.flipButton'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.buttonBackground'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
    iconsAlign: genBrandField({
      as: FieldRadio<IconsAlignEnum>,
      label: t('elements.card.tags.sides.button.position'),
      props: {
        fluid: false,
        hideRadio: true,
        direction: 'horizontal',
        options: ICON_ALIGN_OPTIONS,
      },
    }),
  },
  sideB: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    alignItems: genBrandField({
      as: FieldRadio<'flex-start' | 'center' | 'flex-end'>,
      label: t('input.label.alignment'),
      props: {
        fluid: false,
        options: VERTICAL_ALIGN_OPTIONS,
        hideRadio: true,
        direction: 'horizontal',
      },
    }),
  },
  sideBIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.card.tags.sides.flipButton'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.buttonBackground'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
    iconsAlign: genBrandField({
      as: FieldRadio<IconsAlignEnum>,
      label: t('elements.card.tags.sides.button.position'),
      props: {
        fluid: false,
        hideRadio: true,
        direction: 'horizontal',
        options: ICON_ALIGN_OPTIONS,
      },
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.card.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  animation: {
    type: genBrandField({
      as: FieldSelect<FlipMethodCardEnum>,
      label: t('elements.card.tags.animation.type'),
      props: {
        options: [
          getEnumOption('FlipMethodCardEnum', FlipMethodCardEnum.click),
          getEnumOption('FlipMethodCardEnum', FlipMethodCardEnum.hover),
        ],
      },
    }),
    cardFlip: genBrandField({
      as: FieldSelect<AnimationTypeCardEnum>,
      label: t('elements.card.tags.animation.cardFlip'),
      props: {
        options: [
          getEnumOption('AnimationTypeCardEnum', AnimationTypeCardEnum.horizontalFlip),
          getEnumOption('AnimationTypeCardEnum', AnimationTypeCardEnum.verticalFlip),
          getEnumOption('AnimationTypeCardEnum', AnimationTypeCardEnum.diagonalFlip),
          getEnumOption('AnimationTypeCardEnum', AnimationTypeCardEnum.book),
          getEnumOption('AnimationTypeCardEnum', AnimationTypeCardEnum.fadeEffect),
        ],
      },
    }),
  },
})

const CardFormStyle: CardFormBrandType = ({ device, subConfigs, edited, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.card.tags.card')}</Typography>
        <FormField config={schema.card.height} />
        <FormField config={schema.card.width} />
        <FormField config={schema.card.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />

        <Typography weight='medium'>{t('elements.card.tags.iconButton')}</Typography>
        <FormField config={schema.icon.height} />
        <FormField config={schema.icon.width} />
        <FormField config={schema.icon.borderRadius} />
        <FormField config={schema.hover.backgroundColor} />

        <Typography weight='medium'>{t('elements.card.tags.sideA')}</Typography>
        <FormField config={schema.sideA.backgroundColor} />
        <FormField config={schema.sideA.alignItems} />
        <FormField config={schema.sideAIcon.backgroundImage} />
        <FormField config={schema.sideAIcon.backgroundColor} />
        <FormField config={schema.sideAIcon.color} />
        <FormField config={schema.sideAIcon.iconsAlign} />

        <Typography weight='medium'>{t('elements.card.tags.sideB')}</Typography>
        <FormField config={schema.sideB.backgroundColor} />
        <FormField config={schema.sideB.alignItems} />
        <FormField config={schema.sideBIcon.backgroundImage} />
        <FormField config={schema.sideBIcon.backgroundColor} />
        <FormField config={schema.sideBIcon.color} />
        <FormField config={schema.sideBIcon.iconsAlign} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <Typography weight='medium'>{t('elements.card.tags.animation')}</Typography>
        <FormField config={schema.animation.type} />
        <FormField config={schema.animation.cardFlip} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default CardFormStyle
